import { checkErrors, getDefaultOptions } from "../../services/Category";
import { DeleteRequest, DeleteRequestStatus, Impact } from "./types";

const BASE = "/api/delete-requests";

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/** Raw API payload: snake_case, as DRF serialises it. */
interface DeleteRequestPayload {
  id: number;
  target_property: number;
  target_property_name: string;
  target_uh_cat: number | null;
  target_uh_cat_name: string | null;
  targets_property: boolean;
  requester_name: string;
  created_at: string;
  terminated_at: string | null;
  status: DeleteRequestStatus;
  error: string | null;
  epoch: string;
  days_remaining: number | null;
  can_restore: boolean;
  retention_days: number;
}

const toDeleteRequest = (payload: DeleteRequestPayload): DeleteRequest => ({
  id: payload.id,
  targetProperty: payload.target_property,
  targetPropertyName: payload.target_property_name,
  targetUhCat: payload.target_uh_cat,
  targetUhCatName: payload.target_uh_cat_name,
  targetsProperty: payload.targets_property,
  requesterName: payload.requester_name,
  createdAt: payload.created_at,
  terminatedAt: payload.terminated_at,
  status: payload.status,
  error: payload.error,
  epoch: payload.epoch,
  daysRemaining: payload.days_remaining,
  canRestore: payload.can_restore,
  retentionDays: payload.retention_days,
});

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(
    new URL(`${BASE}${path}`, window.origin).toString(),
    getDefaultOptions(options)
  );
  checkErrors(response);
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Guards against a hung tab if `next` ever pointed back at itself. */
const MAX_PAGES = 50;

export async function fetchDeleteRequests(
  statuses: readonly DeleteRequestStatus[]
): Promise<DeleteRequest[]> {
  // One call per page covers every status asked for: the API takes `status`
  // repeated, `?status=stale&status=error`. A comma-separated list is rejected.
  // Ordering is the model's (`-created_at`), so no client-side sort is needed.
  const params = new URLSearchParams({ page_size: "100" });
  statuses.forEach((status) => params.append("status", status));

  // The whole set is followed, not just the first page. `page_size=100` is the
  // server's maximum, and a mass uh_cat deletion creates one request per room —
  // so a large tenant passes it. Stopping at one page would not just truncate
  // the grid: `siblingsOf` derives an epoch group from what is loaded, so a
  // group split across pages would only be restored in part.
  const payloads: DeleteRequestPayload[] = [];
  let path: string | null = `/?${params}`;

  for (let page = 0; path !== null && page < MAX_PAGES; page += 1) {
    const response: PaginatedResponse<DeleteRequestPayload> = await request<
      PaginatedResponse<DeleteRequestPayload>
    >(path);
    payloads.push(...response.results);
    // Only the query string is taken from `next`: it is an absolute URL built
    // from the request, and reusing its host would follow whatever the proxy
    // reported instead of staying same-origin.
    path =
      response.next === null
        ? null
        : `/${new URL(response.next, window.origin).search}`;
  }

  return payloads.map(toDeleteRequest);
}

export async function fetchDeleteRequest(id: number): Promise<DeleteRequest> {
  return toDeleteRequest(await request<DeleteRequestPayload>(`/${id}/`));
}

export async function restoreDeleteRequest(id: number): Promise<DeleteRequest> {
  const payload = await request<DeleteRequestPayload>(`/${id}/restore/`, {
    method: "POST",
  });
  return toDeleteRequest(payload);
}

export async function createDeleteRequest(target: {
  propertyId?: number;
  uhCatIds?: number[];
}): Promise<DeleteRequest[]> {
  const payload = await request<DeleteRequestPayload[]>("/", {
    method: "POST",
    body: JSON.stringify(
      target.propertyId !== undefined
        ? { target_property: target.propertyId }
        : { target_uh_cats: target.uhCatIds ?? [] }
    ),
  });
  return payload.map(toDeleteRequest);
}

export async function fetchDeletionImpact(target: {
  propertyId?: number;
  uhCatIds?: number[];
}): Promise<Impact> {
  const body =
    target.propertyId !== undefined
      ? { target_property: target.propertyId }
      : { target_uh_cats: target.uhCatIds ?? [] };

  const payload = await request<{
    tables: Impact["tables"];
    total_rows: number;
    dynamic_dependencies: string[];
  }>("/dry-run/", { method: "POST", body: JSON.stringify(body) });

  return {
    tables: payload.tables,
    totalRows: payload.total_rows,
    dynamicDependencies: payload.dynamic_dependencies,
  };
}
